import { useState, useEffect, useRef } from 'react';
import { getExtras, getTiposExtra, createExtra, updateExtra, deleteExtra } from '@helpers/api/mantenedores/extras';

/**
 * Hook para manejar el mantenedor de extras de pagos
 * Maneja filtros, live search y llamadas a la API
 */

export const useExtras = (user, permisos) => {
  const [extras, setExtras] = useState([]);
  const [tiposExtra, setTiposExtra] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [tipoExtraId, setTipoExtraId] = useState(null);

  // Control de cancelación de requests
  const abortControllerRef = useRef();
  const lastFetchId = useRef(0);

  // Obtener tipos de extra al montar el componente
  useEffect(() => {
    async function fetchTiposExtra() {
      try {
        const res = await getTiposExtra(user);
        if (res.success) {
          setTiposExtra(res.data);
        }
      } catch (err) {
        console.error('Error al obtener tipos de extra:', err);
      }
    }
    
    if (user) {
      fetchTiposExtra();
    }
  }, [user]);

  // Efecto para obtener extras con filtros
  useEffect(() => {
    if (!user) return;

    // Cancelar request anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const fetchId = ++lastFetchId.current;

    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const params = {};
        
        if (searchValue.trim()) {
          params.extra = searchValue.trim();
        }
        
        if (tipoExtraId) {
          params.tipo_extra_id = tipoExtraId;
        }

        const res = await getExtras(user, params);
        
        // Solo actualizar si es la última request
        if (lastFetchId.current === fetchId && !controller.signal.aborted) {
          if (res.success) {
            setExtras(res.data);
            setTotal(res.total);
          } else {
            setExtras([]);
            setTotal(0);
            setError(res.error);
          }
        }
      } catch (err) {
        if (lastFetchId.current === fetchId && !controller.signal.aborted) {
          setExtras([]);
          setTotal(0);
          setError('Error al obtener extras');
        }
      }
      
      if (lastFetchId.current === fetchId && !controller.signal.aborted) {
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup: cancelar si cambia dependencia
    return () => {
      controller.abort();
    };
  }, [user, searchValue, tipoExtraId]);

  // Handlers para filtros
  const handleSearchValue = (value) => {
    setSearchValue(value);
  };

  const handleTipoExtraChange = (id) => {
    setTipoExtraId(id);
  };

  // Crear nuevo extra
  const handleCreateExtra = async (extraData) => {
    try {
      const res = await createExtra(user, extraData);
      if (res.success) {
        // Refrescar la lista
        await refetch();
      }
      return res;
    } catch (err) {
      console.error('Error en handleCreateExtra:', err);
      return {
        success: false,
        error: 'Error al crear el extra'
      };
    }
  };

  // Actualizar extra
  const handleUpdateExtra = async (id, extraData) => {
    try {
      const res = await updateExtra(user, id, extraData);
      if (res.success) {
        // Refrescar la lista
        await refetch();
      }
      return res;
    } catch (err) {
      console.error('Error en handleUpdateExtra:', err);
      return {
        success: false,
        error: 'Error al actualizar el extra'
      };
    }
  };

  // Eliminar extra
  const handleDeleteExtra = async (id) => {
    try {
      const res = await deleteExtra(user, id);
      if (res.success) {
        // Refrescar la lista
        await refetch();
      }
      return res;
    } catch (err) {
      console.error('Error en handleDeleteExtra:', err);
      return {
        success: false,
        error: 'Error al eliminar el extra'
      };
    }
  };

  // Función para refrescar datos
  const refetch = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const params = {};
      
      if (searchValue.trim()) {
        params.extra = searchValue.trim();
      }
      
      if (tipoExtraId) {
        params.tipo_extra_id = tipoExtraId;
      }

      const res = await getExtras(user, params);
      
      if (res.success) {
        setExtras(res.data);
        setTotal(res.total);
      } else {
        setExtras([]);
        setTotal(0);
        setError(res.error);
      }
    } catch (err) {
      setExtras([]);
      setTotal(0);
      setError('Error al obtener extras');
    } finally {
      setLoading(false);
    }
  };

  return {
    extras,
    tiposExtra,
    total,
    loading,
    error,
    searchValue,
    tipoExtraId,
    handleSearchValue,
    handleTipoExtraChange,
    handleCreateExtra,
    handleUpdateExtra,
    handleDeleteExtra,
    refetch
  };
};
